/**
 * Session time calculation utilities — THE single source of truth for all
 * timing rules across the Stage Door Access platform.
 *
 * Contents:
 *  • Sat/Sun mode offset table (MODE_OFFSET_MINUTES) with helpers:
 *      resolveSessionTimes, getStartTimeOptions, isMatineeThresholdTriggered
 *  • Monday multi-slot cadence:
 *      MONDAY_SLOT_END_LIMIT, generateDaySlots
 *  • Shared booking class time helpers:
 *      STANDARD_SLOT_KEYS, isStandardSlotKey,
 *      parseTimeSlotTo24h, isCustomTimeClass,
 *      deriveBookingMode, resolveBookingClassTimes
 *
 * DB knobs that drive timing:
 *  • system_settings.default_session_start_time   — baseline (e.g. "10:00:00")
 *  • system_settings.session_duration_minutes      — class length (e.g. 90)
 *  • system_settings.matinee_threshold_time        — threshold (e.g. "13:00:00")
 *  • system_settings.default_matinee_time          — fallback (e.g. "14:00:00")
 *  • tour_stops.monday_start_time                  — first Monday slot (e.g. "09:00:00")
 *  • tour_stops.matinee_time                       — per-venue matinee override
 *
 * Mode is determined by which slots are selected + simultaneous flag:
 *   slot 1 only           → single
 *   slot 2 only           → second_slot
 *   both + !simultaneous  → back_to_back
 *   both + simultaneous   → simultaneous
 *
 * Monday-dated classes bypass session resolution entirely; use generateDaySlots
 * + resolveBookingClassTimes with an explicit startTime instead.
 */
/**
 * Per-mode offset table (minutes relative to defaultSessionStartTime = 10:00).
 *
 * Modes with only 2 options omit 'late' to prevent it from being offered.
 *
 * | Mode         | early    | default  | late     |
 * |--------------|----------|----------|----------|
 * | single       | −30→9:30 |  0→10:00 | +30→10:30|
 * | back_to_back | −90→8:30 | −60→9:00 | (hidden) |
 * | simultaneous | −30→9:30 |  0→10:00 | (hidden) |
 * | second_slot  | +60→11:00| +90→11:30| (hidden) |
 */
const MODE_OFFSET_MINUTES = {
    single: { early: -30, default: 0, late: 30 },
    back_to_back: { early: -90, default: -60 },
    simultaneous: { early: -30, default: 0 },
    second_slot: { early: 60, default: 90 },
};
/**
 * Parse a TIME string (HH:MM or HH:MM:SS) into hours and minutes.
 */
export function parseTime(timeStr) {
    const parts = timeStr.split(':');
    return {
        hours: parseInt(parts[0], 10),
        minutes: parseInt(parts[1], 10),
    };
}
/**
 * Convert hours and minutes to a TIME string (HH:MM:SS).
 */
export function toTimeString(hours, minutes) {
    const h = String(hours).padStart(2, '0');
    const m = String(minutes).padStart(2, '0');
    return `${h}:${m}:00`;
}
/**
 * Add minutes to a TIME string. Returns a new TIME string.
 */
export function addMinutes(timeStr, minutes) {
    const parsed = parseTime(timeStr);
    const totalMinutes = parsed.hours * 60 + parsed.minutes + minutes;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    return toTimeString(newHours, newMinutes);
}
/**
 * Format a TIME string for display: "09:00:00" → "9:00 AM"
 */
export function formatTimeDisplay(timeStr) {
    // Defensive: a missing/empty time must never crash a render — return '' so the
    // UI shows nothing rather than white-screening on parseTime(undefined).
    if (!timeStr)
        return '';
    const { hours, minutes } = parseTime(timeStr);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const displayMinutes = String(minutes).padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${period}`;
}
/**
 * Compare two TIME strings. Returns negative if a < b, 0 if equal, positive if a > b.
 */
export function compareTime(a, b) {
    const pa = parseTime(a);
    const pb = parseTime(b);
    const totalA = pa.hours * 60 + pa.minutes;
    const totalB = pb.hours * 60 + pb.minutes;
    return totalA - totalB;
}
/**
 * Check if matinee threshold is triggered.
 * The effective matinee time is the tour stop's override or the system default.
 * If effective matinee <= threshold, sessions must run simultaneously.
 */
export function isMatineeThresholdTriggered(matineeTime, thresholdTime, defaultMatineeTime) {
    const effectiveMatinee = matineeTime ?? defaultMatineeTime;
    return compareTime(effectiveMatinee, thresholdTime) <= 0;
}
/**
 * Compute the ordered start-time options for a given mode.
 *
 * Returns the preference entries present in that mode's offset table, in
 * chronological order (they're already listed earliest-first in
 * MODE_OFFSET_MINUTES), each paired with the resolved time string.
 * Also returns the default-selected preference for UI pre-selection
 * ('default' for every mode).
 *
 * This is the single source of truth for option sets + times; both the
 * frontend selector and resolveSessionTimes consume it so they never drift.
 */
export function getStartTimeOptions(config, mode) {
    const modeOffsets = MODE_OFFSET_MINUTES[mode];
    const preferenceOrder = ['early', 'default', 'late'];
    const options = preferenceOrder
        .filter((pref) => modeOffsets[pref] !== undefined)
        .map((pref) => ({
        preference: pref,
        startTime: addMinutes(config.defaultSessionStartTime, modeOffsets[pref]),
    }));
    return { options, defaultPreference: 'default' };
}
/**
 * Resolve session start/end times based on booking mode.
 *
 * Business rules:
 * 1. Effective matinee = tour_stop.matinee_time ?? system default.
 * 2. If effective matinee <= threshold → matinee simultaneous forced.
 *    Matinee only promotes back_to_back → simultaneous; single and second_slot
 *    have one session each so matinee override does not apply to them.
 * 3. Anchor offset = MODE_OFFSET_MINUTES[effectiveMode][preference].
 *    If preference is not valid for the mode (e.g. 'late' on back_to_back),
 *    falls back to that mode's 'default' offset (defensive).
 * 4. Mode-specific anchoring:
 *    - single:       session1 starts at anchor; session2 starts at session1.end
 *                    (session2 unused for single but kept sensible).
 *    - back_to_back: session1 starts at anchor (9:00 default / 8:30 early);
 *                    session2 starts at session1.end (zero gap).
 *    - simultaneous: session1 starts at anchor (10:00 default / 9:30 early);
 *                    session2 starts at session1.start.
 *    - second_slot:  session2 starts at its own anchor (11:30 default / 11:00
 *                    early) — NOT derived from session1.end. For the unselected
 *                    Slot-1 preview, session1 anchors to the 'single' default
 *                    (10:00) so the card shows an intuitive 10:00–11:30 range.
 */
export function resolveSessionTimes(config, preference, mode) {
    const matineeTriggered = isMatineeThresholdTriggered(config.matineeTime, config.matineeThresholdTime, config.defaultMatineeTime);
    // Matinee promotes back_to_back to simultaneous; single/second_slot unaffected.
    const effectiveMode = matineeTriggered && mode === 'back_to_back' ? 'simultaneous' : mode;
    const isSimultaneous = effectiveMode === 'simultaneous';
    // Resolve offset: fall back to 'default' if the preference isn't in this mode's table.
    const modeOffsets = MODE_OFFSET_MINUTES[effectiveMode];
    const offset = modeOffsets[preference] ?? modeOffsets['default'];
    const anchor = addMinutes(config.defaultSessionStartTime, offset);
    let session1Start;
    let session1End;
    let session2Start;
    let session2End;
    switch (effectiveMode) {
        case 'single': {
            session1Start = anchor;
            session1End = addMinutes(session1Start, config.sessionDurationMinutes);
            // session2 follows session1 sequentially (unused for single bookings, kept sensible)
            session2Start = session1End;
            session2End = addMinutes(session2Start, config.sessionDurationMinutes);
            break;
        }
        case 'back_to_back': {
            // anchor = 9:00 (default) or 8:30 (early); session2 starts immediately after session1
            session1Start = anchor;
            session1End = addMinutes(session1Start, config.sessionDurationMinutes);
            session2Start = session1End; // zero gap
            session2End = addMinutes(session2Start, config.sessionDurationMinutes);
            break;
        }
        case 'simultaneous': {
            // anchor = 10:00 (default) or 9:30 (early); both sessions start together
            session1Start = anchor;
            session1End = addMinutes(session1Start, config.sessionDurationMinutes);
            session2Start = session1Start;
            session2End = addMinutes(session2Start, config.sessionDurationMinutes);
            break;
        }
        case 'second_slot': {
            // second_slot anchors session2 to its OWN option time (11:30 default / 11:00 early),
            // independent of session1. For the unselected Slot-1 preview card, session1
            // uses the 'single' default offset (0 → 10:00) so it shows an intuitive time.
            session2Start = anchor;
            session2End = addMinutes(session2Start, config.sessionDurationMinutes);
            const singleDefaultOffset = MODE_OFFSET_MINUTES['single']['default'];
            session1Start = addMinutes(config.defaultSessionStartTime, singleDefaultOffset);
            session1End = addMinutes(session1Start, config.sessionDurationMinutes);
            break;
        }
    }
    return {
        session1: { start: session1Start, end: session1End },
        session2: { start: session2Start, end: session2End },
        isSimultaneous,
        matineeTriggered,
    };
}
// =============================================================================
// Monday multi-slot cadence
// =============================================================================
/**
 * Latest end time allowed for Monday slots (exclusive: a slot may END at 21:00
 * but not START at or after it, given a non-zero duration).
 */
export const MONDAY_SLOT_END_LIMIT = '21:00:00';
/**
 * The two standard Sat/Sun session keys used in the booking flow.
 * Anything else is a custom / Monday slot.
 */
export const STANDARD_SLOT_KEYS = ['9:00 AM', '11:00 AM'];
export function isStandardSlotKey(slot) {
    return STANDARD_SLOT_KEYS.includes(slot);
}
/**
 * Generate the sequence of 90-min (or `durationMinutes`-min) Monday time
 * slots from `startTime` up to (but not starting at or past) `endLimit`.
 *
 * Uses **linear total-minutes arithmetic** to avoid the modulo-24h wrap
 * that `addMinutes` applies — never use `addMinutes` inside this loop.
 */
export function generateDaySlots(startTime, durationMinutes, endLimit = MONDAY_SLOT_END_LIMIT) {
    const parsed = parseTime(startTime);
    const endLimitParsed = parseTime(endLimit);
    let slotStart = parsed.hours * 60 + parsed.minutes;
    const endLimitMinutes = endLimitParsed.hours * 60 + endLimitParsed.minutes;
    const slots = [];
    // Linear comparison: slot must fit entirely before endLimit
    while (slotStart + durationMinutes <= endLimitMinutes) {
        const startH = Math.floor(slotStart / 60);
        const startM = slotStart % 60;
        const slotEnd = slotStart + durationMinutes;
        const endH = Math.floor(slotEnd / 60);
        const endM = slotEnd % 60;
        slots.push({
            start: toTimeString(startH, startM),
            end: toTimeString(endH, endM),
        });
        slotStart += durationMinutes;
    }
    return slots;
}
// =============================================================================
// Shared booking class time helpers
// =============================================================================
/**
 * Parse a 12h time string (e.g. "6:00 PM") to 24h format "HH:MM:SS".
 * Returns `null` when the string doesn't match or the hour is out of 1-12 range —
 * callers choose their fallback.
 */
export function parseTimeSlotTo24h(slot) {
    const match = slot.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match)
        return null;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();
    // 12-hour clock: valid hours are 1-12
    if (hours < 1 || hours > 12)
        return null;
    if (period === 'PM' && hours !== 12)
        hours += 12;
    if (period === 'AM' && hours === 12)
        hours = 0;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
}
/**
 * Returns true when this class needs exact-time handling rather than
 * session-1/2 resolution. True when:
 *  • the class date is a Monday (`isMultiSlotDate`), OR
 *  • an explicit `startTime` was supplied, OR
 *  • `timeSlot` is present but is not one of the two standard keys.
 *
 * Stale carts that carry `{sessionIdentifier:'session_2', timeSlot:'6:00 PM'}`
 * on a Monday date are caught by `isMultiSlotDate`.
 */
export function isCustomTimeClass(c, isMultiSlotDate) {
    return isMultiSlotDate || !!c.startTime || (!!c.timeSlot && !isStandardSlotKey(c.timeSlot));
}
/**
 * Derive the SessionMode for a booking from the set of classes requested.
 *
 * Custom-time classes (Monday or explicit startTime) count as neither
 * session_1 nor session_2, so a cart of only Monday classes resolves as
 * 'single' (the stale-cart safe default) and the actual time comes from
 * resolveBookingClassTimes rather than resolveSessionTimes.
 */
export function deriveBookingMode(classes, simultaneous) {
    const hasS1 = classes.some((c) => !isCustomTimeClass(c, c.isMultiSlotDate) &&
        (c.sessionIdentifier === 'session_1' || c.timeSlot === '9:00 AM'));
    const hasS2 = classes.some((c) => !isCustomTimeClass(c, c.isMultiSlotDate) &&
        (c.sessionIdentifier === 'session_2' || c.timeSlot === '11:00 AM'));
    if (hasS1 && hasS2) {
        return simultaneous ? 'simultaneous' : 'back_to_back';
    }
    if (hasS2 && !hasS1)
        return 'second_slot';
    return 'single';
}
/**
 * Resolve the exact start/end times for a single booking class.
 *
 * Branch order (constrain-over-complex — earlier branches remove ambiguity):
 *  (a) `c.startTime` present  → normalize + add duration
 *  (b) custom time class AND `parseTimeSlotTo24h(c.timeSlot)` succeeds → that start + duration
 *  (c) session_2 or '11:00 AM' → resolved.session2
 *  (d) else                    → resolved.session1
 */
export function resolveBookingClassTimes(c, isMultiSlotDate, resolved, durationMinutes) {
    // (a) Explicit 24h startTime — normalize to HH:MM:SS and compute end
    if (c.startTime) {
        // Accept HH:MM or HH:MM:SS; always store as HH:MM:SS
        const normalized = c.startTime.length === 5 ? `${c.startTime}:00` : c.startTime;
        return { start: normalized, end: addMinutes(normalized, durationMinutes) };
    }
    // (b) Custom timeSlot (12h string, e.g. "6:00 PM") — parse to 24h
    if (isCustomTimeClass(c, isMultiSlotDate) && c.timeSlot) {
        const parsed24h = parseTimeSlotTo24h(c.timeSlot);
        if (parsed24h !== null) {
            return { start: parsed24h, end: addMinutes(parsed24h, durationMinutes) };
        }
    }
    // (c) Standard session_2 / '11:00 AM'
    if (c.sessionIdentifier === 'session_2' || c.timeSlot === '11:00 AM') {
        return resolved.session2;
    }
    // (d) Default: session_1
    return resolved.session1;
}
// =============================================================================
// Legacy helpers
// =============================================================================
/**
 * Map old ClassTimeSlot format to SessionIdentifier.
 */
export function mapLegacyTimeSlot(timeSlot) {
    if (timeSlot === '9:00 AM')
        return 'session_1';
    if (timeSlot === '11:00 AM')
        return 'session_2';
    // Already new format
    if (timeSlot === 'session_1' || timeSlot === 'session_2')
        return timeSlot;
    // Fallback
    return 'session_1';
}
//# sourceMappingURL=session-times.js.map